import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { Helmet } from 'react-helmet';

export interface MetaData {
  title?: string;
  description?: string;
  keywords?: string;
  imageUrl?: string;
  url?: string;
  type?: string;
  siteName?: string;
  twitterHandle?: string;
}

interface MetaContextType {
  setMeta: (meta: MetaData) => void;
  resetMeta: () => void;
}

const MetaContext = createContext<MetaContextType | undefined>(undefined);

interface MetaProviderProps {
  children: ReactNode;
}

export const DEFAULT_META: Required<MetaData> = {
  title: process.env.REACT_APP_META_TITLE || '',
  description: process.env.REACT_APP_META_DESCRIPTION || '',
  keywords: process.env.REACT_APP_META_KEYWORDS || '',
  imageUrl: process.env.REACT_APP_PUBLIC_URL || 'https://layerstock.com',
  url: process.env.REACT_APP_PUBLIC_URL || 'https://layerstock.com',
  type: "website",
  siteName: "Layerstock",
  twitterHandle: "@layerstock"
};

export const MetaProvider: React.FC<MetaProviderProps> = ({ children }) => {
  // Default meta values - configure these for your site
  const [meta, setMetaState] = useState<MetaData>({});

  const setMeta = useCallback((newMeta: MetaData) => {
    setMetaState(newMeta);
  }, []);

  const resetMeta = useCallback(() => {
    setMetaState({});
  }, []);
  // Merge current meta with defaults
  const currentMeta = {
    title: meta.title || DEFAULT_META.title,
    description: meta.description || DEFAULT_META.description,
    keywords: meta.keywords || DEFAULT_META.keywords,
    imageUrl: meta.imageUrl || DEFAULT_META.imageUrl,
    url: meta.url || (typeof window !== 'undefined' ? window.location.href : DEFAULT_META.url),
    type: meta.type || DEFAULT_META.type,
    siteName: meta.siteName || DEFAULT_META.siteName,
    twitterHandle: meta.twitterHandle || DEFAULT_META.twitterHandle
  };
  return (
    <MetaContext.Provider value={{ setMeta, resetMeta }}>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{currentMeta.title}</title>
        <meta name="description" content={currentMeta.description} />
        <meta name="keywords" content={currentMeta.keywords} />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content={currentMeta.title} />
        <meta property="og:description" content={currentMeta.description} />
        <meta property="og:image" content={currentMeta.imageUrl} />
        <meta property="og:url" content={currentMeta.url} />
        <meta property="og:type" content={currentMeta.type} />
        <meta property="og:site_name" content={currentMeta.siteName} />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={currentMeta.title} />
        <meta name="twitter:description" content={currentMeta.description} />
        <meta name="twitter:image" content={currentMeta.imageUrl} />
        <meta name="twitter:site" content={currentMeta.twitterHandle} />
        
        {/* Additional SEO Meta Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta httpEquiv="Content-Language" content="en" />
        <link rel="canonical" href={currentMeta.url} />
        
        {/* Structured Data for better SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": currentMeta.siteName,
            "url": currentMeta.url,
            "description": currentMeta.description,
            "potentialAction": {
              "@type": "SearchAction",
              "target": `${currentMeta.url}/assets?search={search_term_string}`,
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>
      {children}
    </MetaContext.Provider>
  );
};

// Custom hook to use meta context
export const useMeta = () => {
  const context = useContext(MetaContext);
  if (context === undefined) {
    throw new Error('useMeta must be used within a MetaProvider');
  }
  return context;
};

// Higher-order component for easy meta setting
interface WithMetaProps {
  title?: string;
  description?: string;
  keywords?: string;
  imageUrl?: string;
  url?: string;
  type?: string;
}

export const withMeta = <P extends object>(
  Component: React.ComponentType<P>,
  metaProps: WithMetaProps
) => {
  return (props: P) => {
    const { setMeta, resetMeta } = useMeta();

    React.useEffect(() => {
      setMeta(metaProps);
      return () => resetMeta();
    }, [setMeta, resetMeta]);

    return <Component {...props} />;
  };
};